import axios from 'axios';
import {
  BillingStatusEnum,
  DeliveryNoteModel,
  DeliveryNoteStateEnum,
} from '../models/DeliveryNoteModel';
import { DeliveryNoteLineRequestModel } from '../models/DeliveryNoteLineRequestModel';
import { UpdateDeliveryNoteRequestModel } from '../models/UpdateDeliveryNoteRequestModel';

const API_URL = process.env.REACT_APP_API_URL;
const DELIVERY_NOTES_URL = `${API_URL}/api/b2b/delivery-notes/`;
const INVOICES_URL = `${API_URL}/api/b2b/invoices/`;

export const DELIVERY_NOTES_PER_PAGE = 20;

interface ListDeliveryNotesResponse {
  results: Array<DeliveryNoteModel>;
  count: number;
}
interface ListDeliveryNotesProps {
  distributorId?: number | null;
  page?: number;
  billingStatus?: BillingStatusEnum | null;
  deliveryNoteStatus: DeliveryNoteStateEnum | null;
  billingAddressId?: number | null;
  since?: string;
  until?: string;
  orderCode?: string;
  minTotal?: number;
  maxTotal?: number;
  externalReference?: string;
  shippingService?: number | null;
}
export function listDeliveryNotes({
  distributorId,
  page = 1,
  billingStatus,
  deliveryNoteStatus,
  billingAddressId,
  since,
  until,
  externalReference,
  minTotal,
  maxTotal,
  orderCode,
  shippingService,
}: ListDeliveryNotesProps): Promise<{ data: ListDeliveryNotesResponse }> {
  return axios.get(DELIVERY_NOTES_URL, {
    params: {
      distributor: distributorId,
      billing_status: billingStatus,
      fiscal_name_id: billingAddressId,
      state: deliveryNoteStatus,
      offset: (page - 1) * DELIVERY_NOTES_PER_PAGE,
      limit: DELIVERY_NOTES_PER_PAGE,
      start_date: since,
      end_date: until,
      external_ref: externalReference,
      min_total: minTotal,
      max_total: maxTotal,
      order_code: orderCode,
      shipping_service: shippingService,
    },
  });
}

export function getDeliveryNoteById(
  id: number
): Promise<{ data: DeliveryNoteModel }> {
  const endpoint = `${DELIVERY_NOTES_URL}${id}/`;
  return axios.get(endpoint);
}

export function createDeliveryNote(orderId, date) {
  return axios.post(DELIVERY_NOTES_URL, {
    order: orderId,
    date,
  });
}

export function updateDeliveryNoteState(deliveryNote: DeliveryNoteModel) {
  const endpoint = `${DELIVERY_NOTES_URL}${deliveryNote.id}/`;

  const body = {
    state: deliveryNote.state,
  };
  return axios.patch(endpoint, body);
}

export function generateEdiFile(deliveryNoteId: number) {
  const endpoint = `${DELIVERY_NOTES_URL}${deliveryNoteId}/generate-edi-file/`;
  return axios.post(endpoint);
}

export function uploadEdiFile(deliveryNoteId: number) {
  const endpoint = `${DELIVERY_NOTES_URL}${deliveryNoteId}/upload-edi-file/`;
  return axios.post(endpoint);
}

export function generatePdfFile(deliveryNoteId: number) {
  const endpoint = `${DELIVERY_NOTES_URL}${deliveryNoteId}/generate-pdf/`;
  return axios.post(endpoint);
}

export function bulkInsertDeliveryNoteLines(
  deliveryNoteId,
  deliveryNoteLines: Array<DeliveryNoteLineRequestModel>
) {
  const endpoint = `${DELIVERY_NOTES_URL}${deliveryNoteId}/delivery-note-lines/`;
  return axios.post(endpoint, { delivery_note_lines: deliveryNoteLines });
}

export interface UpdateDeliveryNoteProps {
  deliveryNoteId: number;
  request: UpdateDeliveryNoteRequestModel;
}
export function updateDeliveryNote({
  deliveryNoteId,
  request,
}: UpdateDeliveryNoteProps) {
  const endpoint = `${DELIVERY_NOTES_URL}${deliveryNoteId}/`;
  return axios.patch(endpoint, request);
}

export enum BulkActionsEnum {
  EMIT_INVOICES = 'emit_bulk_invoices',
}
export function performBulkAction({
  deliveryNoteIds,
  action,
  billing_status,
  distributor,
  delivery_note_status,
  order_code,
  external_ref,
  min_total,
  max_total,
  start_date,
  end_date,
  fiscal_name_id,
  state,
  shipping_service,
}: {
  deliveryNoteIds: Array<number>;
  action: BulkActionsEnum;
  billing_status?: BillingStatusEnum;
  distributor?: number;
  delivery_note_status?: DeliveryNoteStateEnum;
  order_code?: string;
  external_ref?: string;
  min_total?: number;
  max_total?: number;
  start_date?: string;
  end_date?: string;
  fiscal_name_id?: number | null;
  state?: DeliveryNoteStateEnum | null;
  shipping_service?: number | null;
}) {
  return axios.post(`${INVOICES_URL}massive-actions/`, {
    delivery_notes: deliveryNoteIds,
    action,
    billing_status,
    distributor,
    delivery_note_status,
    order_code,
    external_ref,
    min_total,
    max_total,
    start_date,
    end_date,
    fiscal_name_id,
    state,
    shipping_service,
  });
}

export function listFiscalNames() {
  return axios.get(`${API_URL}/api/b2b/fiscal-names/?active=true`);
}
